<template>
  <div class="renderPage lic-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="lic-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-shield-key-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase lic-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Licencias
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="lic-btn font-weight-bold" @click="incluir()">
        <v-icon left small>mdi-plus</v-icon> Nueva Licencia
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="lic-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar compañía o plan..."
          single-line hide-details outlined dense
          class="lic-search"
          style="max-width:400px"
        ></v-text-field>
        <v-spacer />
        <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="lic-table"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.ciaNombre`]="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="font-weight-bold text-body-2">{{ item.ciaNombre }}</span>
            <span class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">{{ item.cia }}</span>
          </div>
        </template>

        <template v-slot:[`item.planNombre`]="{ item }">
          <div class="d-flex align-center" style="gap:6px">
            <span>{{ item.planNombre }}</span>
            <v-chip x-small :color="item.planIlimitado ? 'success' : 'blue-grey'" text-color="white" class="font-weight-bold" label style="border-radius:3px">
              {{ item.planIlimitado ? 'Ilimitado' : 'Limitado' }}
            </v-chip>
          </div>
        </template>

        <template v-slot:[`item.fechaInicio`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.fechaInicio) }}</span>
        </template>

        <template v-slot:[`item.fechaFin`]="{ item }">
          <span :class="isExpired(item.fechaFin) ? 'error--text font-weight-bold' : 'font-weight-medium'">
            {{ formatDate(item.fechaFin) }}
            <v-icon v-if="isExpired(item.fechaFin)" x-small color="error" class="ml-1">mdi-alert-circle</v-icon>
          </span>
        </template>

        <template v-slot:[`item.planMonto`]="{ item }">
          <span class="font-weight-bold">₡{{ Number(item.planMonto).toLocaleString('es-CR', { minimumFractionDigits: 2 }) }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark
            style="min-width:36px;height:36px;padding:0;border-radius:4px"
            @click.stop="editItem(item)">
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-shield-key-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron licencias.</p>
            <v-btn color="primary" depressed small @click="getData()">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DRAWER FORMULARIO -->
    <v-navigation-drawer v-model="drawerForm" fixed right temporary width="520" class="lic-drawer">
      <div class="d-flex flex-column fill-height">

        <!-- HEADER -->
        <div class="lic-drawer-header d-flex align-center px-4 py-3"
             :class="$vuetify.theme.dark ? 'lic-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">{{ editedIndex > -1 ? 'mdi-shield-edit-outline' : 'mdi-shield-plus-outline' }}</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex > -1 ? 'Editar Licencia' : 'Nueva Licencia' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">{{ editedItem.ciaNombre || editedItem.cia || 'Datos de la licencia' }}</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- CUERPO -->
        <div class="flex-grow-1 overflow-y-auto pa-5">
          <v-form v-model="valid" ref="form">

            <div class="lic-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-office-building-outline</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Asignación</span>
            </div>
            <v-card flat class="lic-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.cia"
                    label="Compañía (Cia) *"
                    outlined dense
                    :disabled="editedIndex > -1"
                    :hint="editedIndex > -1 ? 'El código de compañía no se puede modificar' : ''"
                    persistent-hint
                    :rules="[v => !!v || 'Requerido']"
                    :class="editedIndex > -1 ? 'lic-readonly-input' : ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="editedItem.planId"
                    label="Plan *"
                    :items="planes"
                    item-text="nombre"
                    item-value="id"
                    outlined dense
                    :rules="[v => !!v || 'Seleccione un plan']"
                  >
                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center justify-space-between" style="width:100%">
                        <span>{{ item.nombre }}</span>
                        <v-chip x-small :color="item.ilimitado ? 'success' : 'blue-grey'" text-color="white" class="ml-2" label style="border-radius:3px">
                          {{ item.ilimitado ? 'Ilimitado' : 'Limitado' }}
                        </v-chip>
                      </div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-card>

            <div class="lic-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-calendar-range</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Vigencia</span>
            </div>
            <v-card flat class="lic-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.fechaInicio"
                    label="Fecha Inicio *"
                    outlined dense type="date"
                    :rules="[v => !!v || 'Requerido']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.fechaFin"
                    label="Fecha Fin *"
                    outlined dense type="date"
                    :rules="[v => !!v || 'Requerido']"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card>

          </v-form>
        </div>

        <!-- FOOTER -->
        <div class="lic-drawer-footer d-flex align-center px-4 py-3"
             :class="$vuetify.theme.dark ? 'lic-drawer-footer--dark' : ''">
          <v-btn v-if="editedIndex > -1" color="error" text class="lic-btn font-weight-bold" :loading="loadingDelete" @click="deleteItem()">
            <v-icon left small>mdi-trash-can-outline</v-icon> Eliminar
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold lic-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold lic-btn" :loading="loading" @click="save()">
            {{ editedIndex > -1 ? 'GUARDAR' : 'CREAR' }}
          </v-btn>
        </div>

      </div>
    </v-navigation-drawer>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "Licencias",
  components: { Snackbar },
  data() {
    return {
      drawerForm: false,
      loading: false,
      loadingDelete: false,
      valid: false,
      search: "",
      items: [],
      planes: [],
      editedIndex: -1,
      editedItem: {
        cia: "", planId: null, fechaInicio: "", fechaFin: "",
      },
      defaultItem: {
        cia: "", planId: null, fechaInicio: "", fechaFin: "",
      },
      headers: [
        { text: "Compañía",     value: "ciaNombre"                                     },
        { text: "Plan",         value: "planNombre"                                    },
        { text: "Monto",        value: "planMonto",  align: "end"                      },
        { text: "Fecha Inicio", value: "fechaInicio", align: "center", width: "130"    },
        { text: "Fecha Fin",    value: "fechaFin",    align: "center", width: "130"    },
        { text: "Acciones",     value: "actions",  align: "right", sortable: false     },
      ],
      snackText: "",
      snackValue: false,
    };
  },
  computed: {
    ...mapGetters(["user"]),
  },
  created() {
    this.getData();
    this.getPlanes();
  },
  methods: {
    getData() {
      axios.get("api/Licencias/getLicencias")
        .then(r => { this.items = r.data; })
        .catch(e => console.error(e));
    },
    getPlanes() {
      axios.get("api/Licencias/getPlanes")
        .then(r => { this.planes = r.data; })
        .catch(e => console.error(e));
    },
    incluir() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.drawerForm = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = {
        cia:         item.cia,
        ciaNombre:   item.ciaNombre,
        planId:      item.planId,
        fechaInicio: item.fechaInicio ? item.fechaInicio.substring(0, 10) : "",
        fechaFin:    item.fechaFin    ? item.fechaFin.substring(0, 10)    : "",
      };
      this.drawerForm = true;
    },
    cerrar() {
      this.drawerForm = false;
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      const url = this.editedIndex > -1
        ? "api/Licencias/updateLicencia"
        : "api/Licencias/insertLicencia";
      axios.post(url, this.editedItem)
        .then(() => {
          this.snackText = "Licencia guardada correctamente.";
          this.snackValue = true;
          this.getData();
          this.cerrar();
        })
        .catch(e => {
          this.snackText = "Error al guardar la licencia.";
          this.snackValue = true;
          console.error(e);
        })
        .finally(() => { this.loading = false; });
    },
    deleteItem() {
      if (!confirm(`¿Eliminar la licencia de la compañía "${this.editedItem.cia}"?`)) return;
      this.loadingDelete = true;
      axios.get("api/Licencias/deleteLicencia?cia=" + this.editedItem.cia)
        .then(() => {
          this.snackText = "Licencia eliminada.";
          this.snackValue = true;
          this.getData();
          this.cerrar();
        })
        .catch(e => {
          this.snackText = "Error al eliminar la licencia.";
          this.snackValue = true;
          console.error(e);
        })
        .finally(() => { this.loadingDelete = false; });
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return dateStr.substring(0, 10).split("-").reverse().join("/");
    },
    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.lic-container { background-color: #f1f5f9; min-height: 100vh; }
.theme--dark.lic-container { background-color: #0f172a; }

/* ===== TOOLBAR ===== */
.lic-toolbar { border-bottom: 1px solid rgba(0,0,0,0.1) !important; }
.theme--dark .lic-toolbar { border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
.lic-toolbar-title { font-size: 0.95rem !important; letter-spacing: 1.5px; }

/* ===== BUTTONS ===== */
.lic-btn { border-radius: 4px !important; text-transform: uppercase; letter-spacing: 0.8px; font-size: 0.75rem !important; }

/* ===== TABLE ===== */
.lic-table-card { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.lic-table { background: transparent !important; }
.lic-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.lic-table >>> tbody tr { cursor: pointer; }
.lic-table >>> tbody tr:hover { background-color: rgba(55,71,79,0.05) !important; }
.theme--dark .lic-table >>> tbody tr:hover { background-color: rgba(255,255,255,0.05) !important; }

/* ===== SEARCH ===== */
.lic-search >>> .v-input__slot { border-radius: 4px !important; }

/* ===== DRAWER ===== */
.lic-drawer >>> .v-navigation-drawer__content { background-color: #ffffff !important; }
.theme--dark .lic-drawer >>> .v-navigation-drawer__content { background-color: #0f172a !important; }

/* ===== DRAWER HEADER ===== */
.lic-drawer-header { background-color: #37474f; flex-shrink: 0; }
.lic-drawer-header--dark { background-color: #263238 !important; }

/* ===== DRAWER FOOTER ===== */
.lic-drawer-footer { background-color: #f8fafc; border-top: 1px solid rgba(0,0,0,0.08); flex-shrink: 0; }
.lic-drawer-footer--dark { background-color: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }

/* ===== SECTION ===== */
.lic-section-label { display: flex; align-items: center; }
.lic-section-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; background-color: #ffffff !important; }
.theme--dark .lic-section-card { border-color: rgba(255,255,255,0.07) !important; background-color: #1e293b !important; }

/* ===== READONLY INPUT ===== */
.lic-readonly-input >>> .v-input__slot { background-color: rgba(0,0,0,0.03) !important; }
.theme--dark .lic-readonly-input >>> .v-input__slot { background-color: rgba(255,255,255,0.05) !important; }
</style>
